import { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { rotuloModuloAuditoria, rotuloCampoAuditoria, formatarValorAuditoria } from '@/lib/auditoriaEventos';
import { Loader2, Search, ChevronLeft, ChevronRight, Shield, Eye } from 'lucide-react';
import {
  auditLogService,
  AuditLogEntry,
  auditoriaEventoService,
  AuditoriaEventoEntry,
  AuditoriaAcao,
} from '@/services/api';

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const LIMIT = 50;

const ACOES: { value: AuditoriaAcao; label: string }[] = [
  { value: 'criou', label: 'Criou' },
  { value: 'atualizou', label: 'Atualizou' },
  { value: 'excluiu', label: 'Excluiu' },
  { value: 'ativou', label: 'Ativou' },
  { value: 'desativou', label: 'Desativou' },
];

function AcessosTab() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const load = useCallback(async (currentPage: number) => {
    setIsLoading(true);
    const res = await auditLogService.getAll({
      page: currentPage,
      limit: LIMIT,
      search: search || undefined,
      from: from || undefined,
      to: to || undefined,
    });
    if (res.success) {
      setEntries(res.data);
      setTotal(res.total);
    }
    setIsLoading(false);
  }, [search, from, to]);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearch('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome ou e-mail..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="flex gap-3 md:contents">
              <div className="min-w-0 flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">De</label>
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => { setFrom(e.target.value); setPage(1); }}
                  className="w-full min-w-0"
                />
              </div>
              <div className="min-w-0 flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Até</label>
                <Input
                  type="date"
                  value={to}
                  onChange={(e) => { setTo(e.target.value); setPage(1); }}
                  className="w-full min-w-0"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSearch} size="sm">
                <Search className="h-4 w-4 mr-2" />
                Buscar
              </Button>
              <Button onClick={handleClear} variant="outline" size="sm">
                Limpar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium">Acessos registrados</CardTitle>
          <Badge variant="secondary">{total} registros</Badge>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : entries.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">Nenhum registro encontrado</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>E-mail</TableHead>
                    <TableHead className="hidden md:table-cell">IP</TableHead>
                    <TableHead>Data/Hora</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.nome}</TableCell>
                      <TableCell className="text-muted-foreground">{e.email}</TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground text-xs">{e.ip || '—'}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{formatDate(e.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <span className="text-sm text-muted-foreground">Página {page} de {totalPages}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AlteracoesTab() {
  const [entries, setEntries] = useState<AuditoriaEventoEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [modulo, setModulo] = useState<string>('');
  const [acao, setAcao] = useState<string>('');
  const [detalhe, setDetalhe] = useState<AuditoriaEventoEntry | null>(null);
  const [nomes, setNomes] = useState<Record<string, string>>({});
  const [modulosDisponiveis, setModulosDisponiveis] = useState<string[]>([]);

  useEffect(() => {
    auditoriaEventoService.getModulos().then(setModulosDisponiveis);
  }, []);

  const load = useCallback(async (currentPage: number) => {
    setIsLoading(true);
    const res = await auditoriaEventoService.getAll({
      page: currentPage,
      limit: LIMIT,
      search: search || undefined,
      from: from || undefined,
      to: to || undefined,
      modulo: modulo || undefined,
      acao: (acao || undefined) as AuditoriaAcao | undefined,
    });
    if (res.success) {
      setEntries(res.data);
      setNomes(res.nomes ?? {});
      setTotal(res.total);
    }
    setIsLoading(false);
  }, [search, from, to, modulo, acao]);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearch('');
    setFrom('');
    setTo('');
    setModulo('');
    setAcao('');
    setPage(1);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar por nome ou e-mail..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="min-w-0 w-full sm:w-44">
              <label className="text-xs text-muted-foreground mb-1 block">Módulo</label>
              <Select value={modulo || 'todos'} onValueChange={(v) => { setModulo(v === 'todos' ? '' : v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {modulosDisponiveis.map((m) => (
                    <SelectItem key={m} value={m}>{rotuloModuloAuditoria(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0 w-full sm:w-40">
              <label className="text-xs text-muted-foreground mb-1 block">Ação</label>
              <Select value={acao || 'todas'} onValueChange={(v) => { setAcao(v === 'todas' ? '' : v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  {ACOES.map((a) => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0 flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">De</label>
              <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="w-full min-w-0" />
            </div>
            <div className="min-w-0 flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Até</label>
              <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="w-full min-w-0" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSearch} size="sm">
                <Search className="h-4 w-4 mr-2" />
                Buscar
              </Button>
              <Button onClick={handleClear} variant="outline" size="sm">
                Limpar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium">Alterações registradas</CardTitle>
          <Badge variant="secondary">{total} registros</Badge>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : entries.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">Nenhum registro encontrado</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Módulo</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead className="hidden md:table-cell">Entidade</TableHead>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.usuario_nome}</TableCell>
                      <TableCell className="text-muted-foreground">{rotuloModuloAuditoria(e.modulo)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{e.acao}</Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground text-xs">
                        {e.entidade_titulo || e.entidade}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{formatDate(e.createdAt)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => setDetalhe(e)} title="Ver detalhes">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <span className="text-sm text-muted-foreground">Página {page} de {totalPages}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detalhe} onOpenChange={(open) => !open && setDetalhe(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{detalhe?.descricao || 'Detalhes da alteração'}</DialogTitle>
          </DialogHeader>
          {detalhe && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                {detalhe.usuario_nome} ({detalhe.usuario_email}) · {formatDate(detalhe.createdAt)}
              </p>
              {detalhe.alteracoes && Object.keys(detalhe.alteracoes).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(detalhe.alteracoes).map(([campo, valor]) => (
                    <div key={campo} className="rounded-md border border-border p-2">
                      <p className="text-xs font-medium text-muted-foreground">{rotuloCampoAuditoria(campo)}</p>
                      <p>
                        <span className="text-muted-foreground line-through">{formatarValorAuditoria(valor.de, nomes)}</span>
                        {' → '}
                        <span className="font-medium">{formatarValorAuditoria(valor.para, nomes)}</span>
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">Sem detalhes de campos alterados.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AuditLog() {
  return (
    <AppLayout>
      <div className="space-y-4">
        <div className="rounded-lg border bg-card p-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold">Log de Auditoria</h1>
        </div>

        <Tabs defaultValue="acessos">
          <TabsList>
            <TabsTrigger value="acessos">Acessos</TabsTrigger>
            <TabsTrigger value="alteracoes">Alterações</TabsTrigger>
          </TabsList>
          <TabsContent value="acessos" className="mt-4">
            <AcessosTab />
          </TabsContent>
          <TabsContent value="alteracoes" className="mt-4">
            <AlteracoesTab />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
